'use client';

import { useRef, useState } from 'react';
import { INTL_FILE_MAX_BYTES, IntlFileKind } from '@/lib/recruitment/intl-application';
import { INTL_ALL_FILE_LABELS } from '@/lib/recruitment/marketing-application';
import type { FormFileAccept } from '@/lib/recruitment/intl-form-engine';

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';
const RESUME_ACCEPT = '.pdf,.doc,.docx';
const IMAGE_PDF_ACCEPT = `${IMAGE_ACCEPT},application/pdf,.pdf`;

// Client pre-check mirrors the server whitelist (which additionally allows
// HEIC/HEIF for iPhone photos that slip past the accept attribute).
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const RESUME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function isAllowedType(mode: FormFileAccept, file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (mode === 'document') {
    if (RESUME_TYPES.includes(type)) return true;
    // Some Android browsers report an empty MIME for docs - fall back to extension.
    return !type && /\.(pdf|docx?)$/i.test(file.name);
  }
  if (IMAGE_TYPES.includes(type)) return true;
  if (mode === 'image') return false;
  return type === 'application/pdf' || (!type && /\.pdf$/i.test(file.name));
}

const ACCEPT_ATTR: Record<FormFileAccept, string> = {
  document: RESUME_ACCEPT,
  image: IMAGE_ACCEPT,
  image_pdf: IMAGE_PDF_ACCEPT,
  pdf_image: IMAGE_PDF_ACCEPT,
};

const TYPE_ERROR: Record<FormFileAccept, string> = {
  document: 'Only PDF or Word documents are allowed.',
  image: 'Only JPG, PNG, or WebP images are allowed.',
  image_pdf: 'Only JPG or PNG images, or a PDF file, are allowed.',
  pdf_image: 'Only a PDF file or JPG / PNG images are allowed.',
};

const CHOOSE_TEXT: Record<FormFileAccept, { action: string; types: string }> = {
  document: { action: 'Choose a file', types: 'PDF or Word · max 10 MB' },
  image: { action: 'Take or choose a photo', types: 'JPG, PNG, or WebP · max 10 MB' },
  image_pdf: { action: 'Choose an image or PDF', types: 'JPG, PNG, or PDF · max 10 MB' },
  pdf_image: { action: 'Choose a PDF or image', types: 'PDF, JPG, or PNG · max 10 MB' },
};

export function FileUploadCard<K extends string = IntlFileKind>({
  token,
  kind,
  hint,
  capture,
  fileName,
  onUploaded,
  onExpired,
  label: labelOverride,
  accept: acceptMode,
  optional,
}: {
  token: string;
  kind: K;
  /** Small helper text under the label (e.g. what the photo must show). */
  hint?: string;
  /** Pass "user" on the selfie card to open the front camera on phones. */
  capture?: 'user' | 'environment';
  /** Already-uploaded file name from the bootstrap (resume support). */
  fileName: string | null;
  onUploaded: (kind: K, fileName: string, completedSteps: string[]) => void;
  onExpired: () => void;
  /** Card title; defaults to the kind's label. */
  label?: string;
  /** What the slot accepts; defaults to a document for the resume, an image otherwise. */
  accept?: FormFileAccept;
  /** Optional uploads show no required mark. */
  optional?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const mode: FormFileAccept = acceptMode ?? (kind === 'resume' ? 'document' : 'image');
  const accept = ACCEPT_ATTR[mode];
  const label =
    labelOverride ?? (INTL_ALL_FILE_LABELS as Record<string, string>)[kind] ?? String(kind);

  const handleFile = async (file: File) => {
    setError('');
    if (!isAllowedType(mode, file)) {
      setError(TYPE_ERROR[mode]);
      return;
    }
    if (file.size > INTL_FILE_MAX_BYTES) {
      setError('The file is larger than 10 MB. Please choose a smaller file.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/public/intl-recruitment/${token}/files/${kind}`, {
        method: 'POST',
        body: formData,
      });
      if (res.status === 410) {
        onExpired();
        return;
      }
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(d.error || 'Upload failed. Please try again.');
        return;
      }
      onUploaded(kind, d.fileName || file.name, Array.isArray(d.completedSteps) ? d.completedSteps : []);
    } catch {
      setError('Upload failed. Check your connection and try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
      <p className="text-sm font-medium text-gray-200">
        {label} {!optional && <span className="text-red-400">*</span>}
      </p>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture={capture}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      <div className="mt-3">
        {uploading ? (
          <div className="flex items-center gap-3 rounded-xl border border-gray-700 bg-gray-800 px-4 py-3">
            <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-300">Uploading…</p>
          </div>
        ) : fileName ? (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-800 bg-emerald-900/20 px-4 py-3">
            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-emerald-200 truncate flex-1 min-w-0">{fileName}</p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 hover:border-gray-500 rounded-lg px-3 py-1.5 transition-colors flex-shrink-0"
            >
              Replace
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-full rounded-xl border-2 border-dashed border-gray-700 hover:border-emerald-600 px-4 py-5 text-center transition-colors"
          >
            <svg className="w-6 h-6 text-gray-500 mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <span className="block text-sm font-medium text-gray-300">
              {CHOOSE_TEXT[mode].action}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5">{CHOOSE_TEXT[mode].types}</span>
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2 bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-3 py-2 text-xs">
          {error}
        </div>
      )}
    </div>
  );
}
